import { Row } from '@tanstack/react-table'
import { useMemo } from 'react'

export interface FlatData {
  id: string
  parent?: string
}

export type Expandable<TData> = TData & {
  children?: Expandable<TData>[]
  level: number
}

export function transformToExpandableData<TData extends FlatData>(
  flatData: TData[]
): Expandable<TData>[] {
  const idToItemMap = new Map<string, Expandable<TData>>()
  const roots: Expandable<TData>[] = []
  // First pass: Create a map that maps an item's id to the expandable item
  flatData.forEach((data) => {
    idToItemMap.set(data.id, {
      ...data,
      children: [],
      level: 0,
    })
  })

  // Second pass: Build parent-child relationships and set levels
  flatData.forEach((data) => {
    const currentItem = idToItemMap.get(data.id)!
    const parentItem = currentItem.parent
      ? idToItemMap.get(currentItem.parent!)
      : undefined

    if (!parentItem) {
      // If the item has no parent, it is a root item
      roots.push(currentItem)
    } else {
      // If the item has a parent, push the item into the parent's children array
      currentItem.level = parentItem.level + 1
      parentItem.children!.push(currentItem)
    }
  })
  return roots
}

export function getSubRows<TData extends FlatData>(
  originalRow: Expandable<TData>
): Expandable<TData>[] | undefined {
  return originalRow.children && originalRow.children.length > 0
    ? originalRow.children
    : undefined
}

export function getRowCanExpand<TData extends FlatData>(
  row: Row<Expandable<TData>>
): boolean {
  return Boolean(row.original.children && row.original.children.length > 0)
}

export function useDefaultExpandableTableProps<TData extends FlatData>(
  flatData: TData[]
) {
  const data = useMemo(() => transformToExpandableData(flatData), [flatData])

  return {
    data,
    getRowCanExpand,
    getSubRows,
  }
}
